<template>
    <ValidationProvider
        :name="name"
        :rules="rules"
        v-slot="{ errors }"
        :class="[inputType === '1' && 'custom-wrapper', classWrapper]"
        tag="div"
    >
        <div v-if="showTooltip" class="custom-info">
            <div class="position-relative">
                <img
                    class="ico"
                    src="@/assets/images/info-icon.svg"
                    alt="info"
                    @click="toggleShowTooltip"
                />
                <div
                    v-if="isShowTooltip"
                    class="custom-tooltip"
                    v-click-outside="closeTooltip"
                >
                    <div class="custom-tooltip__title">{{ tooltipTitle }}</div>
                    <div class="custom-tooltip__desc">
                        {{ tooltipDesc }}
                    </div>
                    <i
                        class="fa fa-times-circle custom-tooltip__close"
                        aria-hidden="true"
                        @click="closeTooltip"
                    ></i>
                </div>
            </div>
        </div>
        <div
            :class="{
                focus: isFocus,
                'no-label': !label,
                'has-shadow': hasShadow,
                'custom-form-control-type1': inputType === '1',
                'custom-form-control-type2': inputType === '2',
            }"
        >
            <div :class="[`custom-form-control-type${inputType}__prepend-icon`]">
                <slot name="prepend-icon" />
            </div>
            <input
                v-bind="$attrs"
                :value="value"
                @input="onInput"
                @focus="onFocus"
                @blur="onBlur"
                :id="name"
                :disabled="disabled"
                :placeholder="inputType === '1' ? '' : placeholder"
                :class="{
                    'ml-4':
                        typeof $slots['prepend-icon'] !== 'undefined' &&
                        $slots['prepend-icon'].length > 0 &&
                        inputType === '1',
                    'mr-4':
                        typeof $slots['append-icon'] !== 'undefined' &&
                        $slots['append-icon'].length > 0 &&
                        inputType === '1',
                    'pl-4':
                        typeof $slots['prepend-icon'] !== 'undefined' &&
                        $slots['prepend-icon'].length > 0 &&
                        inputType === '2',
                }"
            />
            <label v-if="label" :for="name">{{ label }}</label>
            <div :class="[`custom-form-control-type${inputType}__append-icon`]">
                <slot name="append-icon" />
            </div>
        </div>
        <span :class="[showTooltip && 'show-tooltip']" class="custom-error">{{
            errors[0]
        }}</span>
    </ValidationProvider>
</template>

<script>
export default {
    name: 'TextField',
    data() {
        return {
            isFocus: false,
            isShowTooltip: false,
        };
    },
    props: {
        label: String,
        value: {
            type: [Number, String],
            required: true,
        },
        name: {
            type: String,
            required: true,
        },
        rules: {
            type: [String, Array, Object],
        },
        classWrapper: {
            type: String,
        },
        hasShadow: {
            type: Boolean,
            default: false,
        },
        disabled: Boolean,
        showTooltip: Boolean,
        tooltipTitle: String,
        tooltipDesc: String,
        inputType: {
            type: String,
            default: '1',
        },
        placeholder: String,
    },
    methods: {
        onInput(event) {
            this.$emit('input', event.target.value);
        },
        onFocus(event) {
            this.isFocus = true;
            this.$emit('focus', event.target.value);
        },
        onBlur(event) {
            this.isFocus = false;
            this.$emit('focus', event.target.value);
        },
        toggleShowTooltip() {
            this.isShowTooltip = !this.isShowTooltip;
        },
        closeTooltip() {
            this.isShowTooltip = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.custom-wrapper {
    min-height: 90px;
    display: flex;
    flex-wrap: wrap;

    &.none-min-height {
        min-height: 0;
    }
}
.custom-form-control-type1 {
    background-color: #e1e7e9;
    padding: 24px 16px 8px 16px;
    border-radius: 12px;
    position: relative;
    height: 59px;
    box-shadow: 0px 13px 30px -12px rgba(0, 0, 0, 0.25);
    flex-grow: 1;

    &.no-label {
        padding: 16px;
    }

    &.focus {
        background-color: #e1e7e9;
    }

    &.has-shadow {
        box-shadow: 0px 15px 16px -17px #767676;
    }

    &.disabled {
        opacity: 0.4;
    }

    label {
        color: #969fa2;
        font-size: 14px;
        position: absolute;
        margin-bottom: 0;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        padding: 16px;
        transition: all 0.1s ease-in-out;
        cursor: text;
        display: flex;
        align-items: center;
    }

    input {
        border: none;
        background: none;
        font-size: 18px;
        color: rgba(51, 48, 60, 0.68);
        padding: 0;
        width: 100%;

        &::placeholder {
            visibility: hidden;
        }

        &:focus + label,
        &:not(:placeholder-shown) + label {
            font-size: 13px;
            opacity: 0.7;
            padding-block: 0;
            bottom: 24px;
            color: #1b659e;
        }
    }

    &__prepend-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        left: 15px;
        z-index: 3;
        display: inline-block;
    }

    &__append-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        right: 15px;
        z-index: 3;
        display: inline-block;
    }
}
.custom-form-control-type2 {
    width: 100%;
    position: relative;
    height: 40px;
    input {
        background: none;
        font-size: 16px;
        color: #7c7c7a;
        padding-inline: 16px;
        width: 100%;
        border: 1px solid #7c7c7a;
        border-radius: 8px;
        height: 100%;

        &::placeholder {
            color: #7c7c7a !important;
            opacity: 1;
        }
    }
    &__prepend-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: 15px;
        z-index: 3;
        display: inline-block;
    }

    &__append-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 15px;
        z-index: 3;
        display: inline-block;
    }
}
.custom-error {
    color: var(--danger);
    font-size: 12px;
    width: 100%;
    &.show-tooltip {
        margin-left: 30px;
    }
}
.custom-info {
    .ico {
        color: #7bbdef;
        font-size: 20px;
        cursor: pointer;
        margin-right: 8px;
        margin-top: 20px;
    }
}
.custom-tooltip {
    position: absolute;
    border-radius: 30px;
    background: #edf5f7;
    box-shadow: 0px 13px 30px -12px rgba(0, 0, 0, 0.25);
    min-width: 400px;
    padding: 20px;
    z-index: 10;
    transform: translate(30px, -60px);

    &__title {
        font-size: 16px;
        font-weight: 600;
        line-height: 24px; /* 150% */
        color: #41b8dc;
        margin-bottom: 8px;
    }

    &__desc {
        font-size: 16px;
        line-height: 24px; /* 150% */
        color: #7c7c7a;
    }

    &__close {
        position: absolute;
        right: 16px;
        top: 8px;
        color: #2178ab !important;
        cursor: pointer;
    }
}
</style>
